import { LitElement, html, css } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';

export class MessageBox extends LitElement {
    static get styles() {
        return css`
        
        :host {
            display: none; /* 初期状態では非表示 */
            position: fixed;
            top: 0;
            left: 0;
            min-width: 200vw;
            min-height: 100vh;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 2500;
        }

        :host([visible]) {
            display: block; /* visible属性がtrueの時に表示 */
        }
        
        @keyframes slideDown {
            from {
              transform: translateY(-100%) translateX(-50%);
              opacity: 0;
            }
            to {
              transform: translateY(-50%) translateX(-50%);
              opacity: 1;
            }
          }
          
        @keyframes slideUp {
            from {
                transform: translateY(-50%) translateX(-50%);
                opacity: 1;
            }
            to {
                transform: translateY(-100%) translateX(-50%);
                opacity: 0;
            }
        }

        .message-box-content {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 300px; /* デフォルト幅。boxWidthで上書き可能 */
            max-width: 90vw; /* モバイル対応：画面幅からはみ出るのを防ぐ */
            /* height は boxHeight で指定されるか、コンテンツに依存 */
            border-radius: 8px;
            padding: 24px;
            background-color: #fff;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
            box-sizing: border-box;
            color: #333;
            font-family: Arial, sans-serif;
            user-select: none;
        }

        .overlay {
            position: fixed;
        }

        .button-group {
            display: flex;
            justify-content: space-evenly;
        }
    `;
    }

    static properties = {
        visible: { type: Boolean },
        boxWidth: { type: String },
        boxHeight: { type: String },
    };

    constructor() {
        super();
        this.visible = false;
        this.boxWidth = null;
        this.boxHeight = null;
    }

    connectedCallback() {
        super.connectedCallback();
        // passive: false でイベントを登録することで e.preventDefault() の呼び出しを可能にします
        this._boundPreventPullToRefresh = this._preventPullToRefresh.bind(this);
        this.addEventListener('touchmove', this._boundPreventPullToRefresh, { passive: false });

        this._boundStopPropagation = (e) => {
            e.stopPropagation();
        };

        this.addEventListener('mousedown', this._boundStopPropagation);
        this.addEventListener('touchstart', this._boundStopPropagation, { passive: true });
        this.addEventListener('mousemove', this._boundStopPropagation);
        this.addEventListener('mouseup', this._boundStopPropagation);
        this.addEventListener('touchend', this._boundStopPropagation);
    }

    disconnectedCallback() {
        this.removeEventListener('touchmove', this._boundPreventPullToRefresh);
        this.removeEventListener('mousedown', this._boundStopPropagation);
        this.removeEventListener('touchstart', this._boundStopPropagation);
        this.removeEventListener('mousemove', this._boundStopPropagation);
        this.removeEventListener('mouseup', this._boundStopPropagation);
        this.removeEventListener('touchend', this._boundStopPropagation);
        super.disconnectedCallback();
    }

    /**
     * モバイルブラウザでの意図しない「引っ張って更新」を防止する
     */
    _preventPullToRefresh(e) {
        if (!this.visible) return;

        const scrollWrapper = this.shadowRoot.querySelector('.message-content-wrapper');
        const path = e.composedPath();

        // 内部にスクロール可能な領域があり、その上での操作であるか判定
        if (scrollWrapper && path.includes(scrollWrapper)) {
            const scrollTop = scrollWrapper.scrollTop;
            const scrollHeight = scrollWrapper.scrollHeight;
            const clientHeight = scrollWrapper.clientHeight;

            // スクロールが必要ない場合、またはマルチタッチ（ピンチ操作など）時はイベントをブロック
            if (e.touches.length > 1 || scrollHeight <= clientHeight) {
                e.preventDefault();
            }
            return;
        }

        // スクロール領域外（背景のオーバーレイやボタンなど）でのタッチ移動は、
        // ブラウザへの伝播を防ぐためにデフォルトの挙動を無効化します
        e.preventDefault();
    }

    updated(changedProperties) {
        if (changedProperties.has('visible')) {
            const messageBoxContent = this.shadowRoot.querySelector('.message-box-content');
            if (messageBoxContent) {
                messageBoxContent.style.animation = this.visible ? 'slideDown 0.3s ease-in-out forwards' : 'slideUp 0.3s ease-in-out forwards';
            }

            if (this.visible) {
                this.setAttribute('visible', '');
            } else {
                // アニメーションが終了するのを待ってから非表示にする
                setTimeout(() => {
                    this.removeAttribute('visible');
                }, 300);
            }
        }
    }

    show() {
        this.visible = true;
        return new Promise((resolve) => {
            this.updateComplete.then(() => {
                const focusTarget = this.querySelector('[slot="cancel-slot"]') || this.querySelector('[slot="ok-slot"]');

                if (focusTarget) {
                    setTimeout(() => focusTarget.focus(), 10);
                }

                const yesButton = this.querySelector('[slot="ok-slot"]') || this.shadowRoot.querySelector('[name="ok-slot"]');
                const noButton = this.querySelector('[slot="cancel-slot"]') || this.shadowRoot.querySelector('[name="cancel-slot"]');
                const overlay = this.shadowRoot.querySelector('.overlay');

                if (!yesButton || !noButton) {
                    console.warn('MessageBox: OK or Cancel button slot content not found.');
                    return;
                }

                const yesListener = (e) => {
                    resolve(true);
                    cleanup();
                    this.hide();
                    e.stopPropagation();
                };

                const noListener = (e) => {
                    resolve(false);
                    cleanup();
                    this.hide();
                    e.stopPropagation();
                };

                // 背景（オーバーレイ）やダイアログ枠外のクリック時にも、
                // キャンセル(false)としてPromiseを正常解決させます
                const closeListener = (e) => {
                    resolve(false);
                    cleanup();
                    this.hide();
                    e.stopPropagation();
                };

                const cleanup = () => {
                    yesButton.removeEventListener('click', yesListener);
                    noButton.removeEventListener('click', noListener);
                    this.removeEventListener('click', closeListener);
                    if (overlay) {
                        overlay.removeEventListener('click', closeListener);
                    }
                };

                yesButton.addEventListener('click', yesListener);
                noButton.addEventListener('click', noListener);
                this.addEventListener('click', closeListener);
                if (overlay) {
                    overlay.addEventListener('click', closeListener);
                }
            });
        });
    }

    hide() {
        this.visible = false;
    }

    _handleOverlayClick(e) {
        this.hide();
    }

    _handleContentClick(e) {
        e.stopPropagation();
    }

    render() {
        const contentStyles = {};
        if (this.boxWidth) {
            contentStyles.width = this.boxWidth;
        }

        if (this.boxHeight) {
            contentStyles.height = this.boxHeight;
        }

        const wrapperClasses = {
            'message-content-wrapper': true,
            'custom-height': !!this.boxHeight,
        };

        return html`
        <div class="overlay"></div>
        <div class="message-box-content" style=${styleMap(contentStyles)} @click="${this._handleContentClick}">
            <div class=${classMap(wrapperClasses)}>
                <slot name="message-slot"></slot>
            </div>
            <div class="button-group">
                <slot name="ok-slot"></slot>
                <slot name="cancel-slot"></slot>
            </div>
        </div>
    `;
    }
}

customElements.define('message-box', MessageBox);